"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { getTokens } from "@/lib/auth"
import { useUser } from "@/components/user-context"
import {
  loadDialogs as fetchDialogsFromApi,
  loadProjects as fetchProjectsFromApi,
  deleteDialog,
  cloneDialog,
  renameDialog,
  moveDialogToProject,
  createProject as createProjectApi,
  renameProject as renameProjectApi,
  deleteProject as deleteProjectApi,
  copilotErrorMessage,
  DIALOG_RUNS_CHANGED_EVENT,
  DIALOGS_CHANGED_EVENT,
  type ChatProject,
  type DialogItem,
} from "@/lib/copilot-chat"
import { groupDialogsByProject, visibleProjectGroups } from "@/lib/chat-projects"

/** Пока в каком-то чате работает агент — как часто обновлять список */
const RUNNING_REFRESH_MS = 5000

const expandedKey = (ws: number) => `casper:chat-projects-expanded:${ws}`
const showAllKey = (ws: number) => `casper:chat-projects-show-all:${ws}`

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function writeJson(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)) } catch {}
}

export const cleanChatName = (value: string) => value.replace(/\s+/g, " ").trim()

export interface UseChatListOptions {
  activeDialogId: string | null
  /** Открыть чат в копилоте (после клонирования — копию) */
  openDialog: (dialogId: string) => void
  /** Удалён открытый чат — копилот переходит к новому */
  onActiveDialogDeleted: () => void
  /** Опрашивать список, пока работает агент (мобильный список опрашивается, только когда открыт) */
  pollWhileRunning?: boolean
  /** Область событий чата (CopilotV15Config.eventScope), которому адресован «Новый чат в проекте» */
  eventScope?: string
}

/** Чаты и папки проектов копилота: загрузка, обновление по событиям и действия — общие для десктопа и мобильной версии */
export function useChatList({ activeDialogId, openDialog, onActiveDialogDeleted, pollWhileRunning = true, eventScope }: UseChatListOptions) {
  const { userWorkspace } = useUser()
  const wsId = userWorkspace?.workspace?.id

  const [dialogs, setDialogs] = useState<DialogItem[]>([])
  const [projects, setProjects] = useState<ChatProject[]>([])
  // true только после первого успешного завершения загрузки
  const [hasFetched, setHasFetched] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [showAllProjects, setShowAllProjects] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!wsId) return
    setExpanded(new Set(readJson<string[]>(expandedKey(wsId), [])))
    setShowAllProjects(readJson<boolean>(showAllKey(wsId), false))
  }, [wsId])

  const reload = useCallback(async () => {
    const token = getTokens()?.accessToken
    if (!token || !wsId) return

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    try {
      const [fetchedDialogs, fetchedProjects] = await Promise.all([
        fetchDialogsFromApi(wsId, token, controller.signal),
        fetchProjectsFromApi(wsId, token, controller.signal),
      ])
      if (controller.signal.aborted) return
      setDialogs(fetchedDialogs)
      setProjects(fetchedProjects)
    } catch (e) {
      if ((e as Error | undefined)?.name === "AbortError") return
    } finally {
      if (!controller.signal.aborted) setHasFetched(true)
    }
  }, [wsId])

  useEffect(() => {
    reload()
    return () => { abortRef.current?.abort() }
  }, [reload])

  // Новый чат, ветка, копия, перемещение, запуск агента — обновляем список
  useEffect(() => {
    const later = (ms: number) => () => { setTimeout(reload, ms) }
    const onNewChat = later(600)
    const onRuns = later(400)
    const onChanged = later(0)
    window.addEventListener("casper:new-chat", onNewChat)
    window.addEventListener(DIALOG_RUNS_CHANGED_EVENT, onRuns)
    window.addEventListener(DIALOGS_CHANGED_EVENT, onChanged)
    return () => {
      window.removeEventListener("casper:new-chat", onNewChat)
      window.removeEventListener(DIALOG_RUNS_CHANGED_EVENT, onRuns)
      window.removeEventListener(DIALOGS_CHANGED_EVENT, onChanged)
    }
  }, [reload])

  const anyRunning = dialogs.some((d) => d.running)
  useEffect(() => {
    if (!anyRunning || !pollWhileRunning) return
    const timer = setInterval(reload, RUNNING_REFRESH_MS)
    return () => clearInterval(timer)
  }, [anyRunning, pollWhileRunning, reload])

  const { groups, recent } = useMemo(() => groupDialogsByProject(dialogs, projects), [dialogs, projects])
  const activeProjectId = useMemo(() => {
    const d = dialogs.find((x) => x.dialogId === activeDialogId)
    return d?.projectId && projects.some((p) => p.id === d.projectId) ? d.projectId : null
  }, [dialogs, projects, activeDialogId])
  const { visible: visibleGroups } = visibleProjectGroups(groups, { showAll: showAllProjects, activeProjectId })

  const setExpandedPersist = useCallback((update: (prev: Set<string>) => Set<string>) => {
    setExpanded((prev) => {
      const next = update(prev)
      if (wsId) writeJson(expandedKey(wsId), [...next])
      return next
    })
  }, [wsId])

  const expandProject = useCallback((id: string) => {
    setExpandedPersist((prev) => (prev.has(id) ? prev : new Set(prev).add(id)))
  }, [setExpandedPersist])

  // Папка открытого чата раскрыта — чат виден в списке
  useEffect(() => {
    if (activeProjectId) expandProject(activeProjectId)
  }, [activeProjectId, expandProject])

  const toggleProject = (id: string) => {
    setExpandedPersist((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleShowAll = () => {
    setShowAllProjects((v) => {
      if (wsId) writeJson(showAllKey(wsId), !v)
      return !v
    })
  }

  const startNewChatInProject = (project: ChatProject) => {
    expandProject(project.id)
    window.dispatchEvent(new CustomEvent("casper:new-chat", { detail: { projectId: project.id, projectName: project.name, scope: eventScope } }))
  }

  const withToken = async (id: string, action: (token: string) => Promise<void>, errorText: string) => {
    const token = getTokens()?.accessToken
    if (!token) return
    setBusyId(id)
    try {
      await action(token)
    } catch (e) {
      toast.error(copilotErrorMessage(e, errorText))
    } finally {
      setBusyId(null)
    }
  }

  const deleteChat = (dialogId: string) => withToken(dialogId, async (token) => {
    const ok = await deleteDialog(dialogId, token)
    if (!ok) throw new Error("delete failed")
    setDialogs((prev) => prev.filter((d) => d.dialogId !== dialogId))
    toast.success("Чат удалён")
    if (dialogId === activeDialogId) onActiveDialogDeleted()
  }, "Не удалось удалить чат")

  const cloneChat = (dialog: DialogItem) => withToken(dialog.dialogId, async (token) => {
    const copy = await cloneDialog(dialog.dialogId, token)
    await reload()
    toast.success(`Создана копия «${dialog.title || "Без названия"}»`)
    openDialog(copy.dialogId)
  }, "Не удалось клонировать чат")

  const moveChat = (dialog: DialogItem, projectId: string | null) => {
    if ((dialog.projectId ?? null) === projectId) return
    const previous = dialog.projectId ?? null
    setDialogs((prev) => prev.map((d) => (d.dialogId === dialog.dialogId ? { ...d, projectId } : d)))
    if (projectId) expandProject(projectId)
    return withToken(dialog.dialogId, async (token) => {
      try {
        await moveDialogToProject(dialog.dialogId, projectId, token)
      } catch (e) {
        setDialogs((prev) => prev.map((d) => (d.dialogId === dialog.dialogId ? { ...d, projectId: previous } : d)))
        throw e
      }
      const name = projects.find((p) => p.id === projectId)?.name
      toast.success(projectId ? `Чат перемещён в «${name ?? "проект"}»` : "Чат убран из проекта")
    }, "Не удалось переместить чат")
  }

  const renameChat = (id: string, value: string) => {
    const name = cleanChatName(value)
    const current = dialogs.find((d) => d.dialogId === id)
    if (!name || !current || name === current.title) return
    setDialogs((prev) => prev.map((d) => (d.dialogId === id ? { ...d, title: name } : d)))
    return withToken(id, async (token) => {
      try {
        await renameDialog(id, name, token)
      } catch (e) {
        setDialogs((prev) => prev.map((d) => (d.dialogId === id ? { ...d, title: current.title } : d)))
        throw e
      }
    }, "Не удалось переименовать чат")
  }

  const renameProject = (id: string, value: string) => {
    const name = cleanChatName(value)
    const current = projects.find((p) => p.id === id)
    if (!name || !current || name === current.name) return
    setProjects((prev) => prev.map((p) => (p.id === id ? { ...p, name } : p)))
    return withToken(id, async (token) => {
      try {
        await renameProjectApi(id, name, token)
      } catch (e) {
        setProjects((prev) => prev.map((p) => (p.id === id ? { ...p, name: current.name } : p)))
        throw e
      }
    }, "Не удалось переименовать проект")
  }

  const createProject = async (name: string, moveDialogId?: string) => {
    const token = getTokens()?.accessToken
    if (!token || !wsId) return false
    try {
      const project = await createProjectApi(wsId, name, token)
      setProjects((prev) => [project, ...prev])
      expandProject(project.id)
      if (moveDialogId) {
        await moveDialogToProject(moveDialogId, project.id, token)
        setDialogs((prev) => prev.map((d) => (d.dialogId === moveDialogId ? { ...d, projectId: project.id } : d)))
        toast.success(`Проект «${project.name}» создан, чат перемещён`)
      } else {
        toast.success(`Проект «${project.name}» создан`)
      }
      return true
    } catch (e) {
      toast.error(copilotErrorMessage(e, "Не удалось создать проект"))
      return false
    }
  }

  const deleteProject = (project: ChatProject) => withToken(project.id, async (token) => {
    await deleteProjectApi(project.id, token)
    setProjects((prev) => prev.filter((p) => p.id !== project.id))
    setDialogs((prev) => prev.map((d) => (d.projectId === project.id ? { ...d, projectId: null } : d)))
    toast.success(`Проект «${project.name}» удалён, его чаты — в «Недавних»`)
  }, "Не удалось удалить проект")

  return {
    dialogs,
    projects,
    hasFetched,
    busyId,
    groups,
    recent,
    visibleGroups,
    expanded,
    showAllProjects,
    reload,
    toggleProject,
    toggleShowAll,
    startNewChatInProject,
    deleteChat,
    cloneChat,
    moveChat,
    renameChat,
    renameProject,
    createProject,
    deleteProject,
  }
}
